import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { useLanguage } from '../i18n/LanguageProvider';
import { brandLogoUrl, buildColors, type Brand } from './brand';
import { getBrand, subscribeBrand } from './brandStore';
import { fontPresetStylesheetUrl, fontsForPreset } from './fontPresets';
import { colorsToCssVars, type ThemeColors, type ThemeName } from './tokens';

export type ThemePreference = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'elenthor.portal.theme';
const PRESET_LINK_ID = 'brand-font-preset';

type ThemeContextValue = {
  theme: ThemeName;
  preference: ThemePreference;
  setPreference: (next: ThemePreference) => void;
  isDark: boolean;
  colors: ThemeColors;
  brand: Brand | null;
  /** The tenant's logo for the active scheme, or null → render the wordmark / name. */
  logoUrl: string | null;
  /** What to call the product in the UI: the tenant's name, or the platform's. */
  displayName: string | null;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

/**
 * Falls back to 'light', not 'system', when nothing is stored.
 *
 * Product decision: the branded portal should present its light identity to
 * first-time visitors. Following the OS preference meant a dark-mode machine
 * loaded the portal dark on first visit, making first impressions inconsistent
 * with the marketing and proposal screenshots. An explicit choice — including
 * 'system' and 'dark' from the appearance toggle — is still read and honoured.
 */
function readPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'light';
  } catch {
    return 'light';
  }
}

function useSystemScheme(): ThemeName {
  const query = useMemo(() => (typeof window !== 'undefined' ? window.matchMedia('(prefers-color-scheme: dark)') : null), []);
  const subscribe = useCallback(
    (cb: () => void) => {
      query?.addEventListener('change', cb);
      return () => query?.removeEventListener('change', cb);
    },
    [query],
  );
  return useSyncExternalStore(subscribe, () => (query?.matches ? 'dark' : 'light'), () => 'light');
}

/**
 * Resolves theme × brand × direction into CSS custom properties on `<html>`,
 * the single place the stylesheet and Tailwind read colour and type from.
 * Sits inside LanguageProvider so an Arabic render swaps the font roles the
 * same way the app does (no Latin preset has Arabic coverage).
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const { direction } = useLanguage();
  const systemScheme = useSystemScheme();
  const [preference, setPreferenceState] = useState<ThemePreference>(readPreference);
  const brand = useSyncExternalStore(subscribeBrand, getBrand, getBrand);

  const theme: ThemeName = preference === 'system' ? systemScheme : preference;
  const colors = useMemo(() => buildColors(theme, brand?.palette ?? null), [theme, brand]);
  const fonts = useMemo(() => fontsForPreset(brand?.fontPreset, direction), [brand?.fontPreset, direction]);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* session-only */
    }
  }, []);

  useLayoutEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = theme;
    root.style.colorScheme = theme;
    for (const [name, value] of Object.entries(colorsToCssVars(colors))) root.style.setProperty(name, value);
    root.style.setProperty('--font-sans', fonts.sans);
    root.style.setProperty('--font-serif', fonts.serif);
    root.style.setProperty('--font-serif-weight', fonts.serifWeight);
    let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'theme-color';
      document.head.appendChild(meta);
    }
    meta.content = colors.surface;
  }, [theme, colors, fonts]);

  // A tenant preset's faces load on demand; the default pairing ships in index.html.
  useEffect(() => {
    const href = fontPresetStylesheetUrl(brand?.fontPreset);
    let link = document.getElementById(PRESET_LINK_ID) as HTMLLinkElement | null;
    if (!href) {
      link?.remove();
      return;
    }
    if (!link) {
      link = document.createElement('link');
      link.id = PRESET_LINK_ID;
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
    if (link.href !== href) link.href = href;
  }, [brand?.fontPreset]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      preference,
      setPreference,
      isDark: theme === 'dark',
      colors,
      brand,
      logoUrl: brandLogoUrl(brand, theme),
      displayName: brand?.displayName ?? null,
    }),
    [theme, preference, setPreference, colors, brand],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
}
